import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Zap, MapPin, User, Phone, ExternalLink, Send } from "lucide-react";
import { useInstantQuote } from "@/hooks/useInstantQuote";
import { Card, CardContent } from "@/components/ui/card";
import { AddressAutocomplete } from "@/components/measurements/AddressAutocomplete";

interface DoorKnockerQuickQuoteProps {
  trigger?: React.ReactNode;
}

export function DoorKnockerQuickQuote({ trigger }: DoorKnockerQuickQuoteProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");

  const { generateInstantQuote, isGenerating, progress, result } = useInstantQuote();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim() || !address.trim()) {
      return;
    }

    await generateInstantQuote(name.trim(), address.trim(), phone.trim() || undefined);
  };

  const handleReset = () => {
    setName("");
    setAddress("");
    setPhone("");
  };

  const handleViewProposal = () => {
    if (result?.proposal.shareId) {
      navigate(`/proposal/${result.proposal.shareId}`);
      setOpen(false);
    }
  };

  const handleOpenNewTab = () => {
    if (result?.proposal.shareId) {
      window.open(`/proposal/${result.proposal.shareId}`, '_blank');
    }
  };

  const handleNewQuote = () => {
    handleReset();
  };

  const defaultTrigger = (
    <Button className="gap-2">
      <Zap className="h-4 w-4" />
      Instant Quote
    </Button>
  );

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        {trigger || defaultTrigger}
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            Instant Quote
          </SheetTitle>
          <SheetDescription>
            Enter customer details to generate a quote with Good/Better/Best pricing instantly.
          </SheetDescription>
        </SheetHeader>

        {!result ? (
          <form onSubmit={handleSubmit} className="space-y-6 mt-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  Customer Name
                </Label>
                <Input
                  id="name"
                  placeholder="John Smith"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isGenerating}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="address" className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  Property Address
                </Label>
                <AddressAutocomplete
                  value={address}
                  onChange={setAddress}
                  onPlaceSelected={({ address: addr }) => setAddress(addr)}
                  isLoaded={true}
                  disabled={isGenerating}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  Phone Number (optional)
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  disabled={isGenerating}
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full gap-2" 
              size="lg"
              disabled={isGenerating || !name.trim() || !address.trim()}
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {progress || "Generating..."}
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4" />
                  Generate Instant Quote
                </>
              )}
            </Button>

            {isGenerating && (
              <div className="text-center text-sm text-muted-foreground">
                <p>🏠 Measuring roof via satellite...</p>
                <p>💰 Calculating Good/Better/Best pricing...</p>
                <p>📄 Building proposal with neighborhood map...</p>
              </div>
            )}
          </form>
        ) : (
          <div className="space-y-6 mt-6">
            {/* Success State */}
            <Card className="border-primary/50 bg-primary/5">
              <CardContent className="pt-6">
                <div className="text-center space-y-2">
                  <div className="text-4xl">🎉</div>
                  <h3 className="font-semibold text-lg">Quote Ready!</h3>
                  <p className="text-sm text-muted-foreground">
                    {result.measurement.totalSquares.toFixed(1)} squares measured
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Pricing Summary */}
            <div className="grid grid-cols-3 gap-2">
              <Card>
                <CardContent className="p-3 text-center">
                  <p className="text-xs text-muted-foreground">Good</p>
                  <p className="font-bold text-lg text-primary/70">
                    ${result.pricing.good.toLocaleString()}
                  </p>
                </CardContent>
              </Card>
              <Card className="border-primary">
                <CardContent className="p-3 text-center">
                  <p className="text-xs text-muted-foreground">Better</p>
                  <p className="font-bold text-lg text-primary">
                    ${result.pricing.better.toLocaleString()}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 text-center">
                  <p className="text-xs text-muted-foreground">Best</p>
                  <p className="font-bold text-lg text-primary/90">
                    ${result.pricing.best.toLocaleString()}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Customer Info */}
            <Card>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{result.lead.name}</span>
                </div>
                <div className="flex items-start gap-2 text-sm">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <span className="text-muted-foreground">{result.lead.address}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-2">
                  📡 Source: {result.measurement.source}
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="space-y-2">
              <Button onClick={handleViewProposal} className="w-full gap-2" size="lg">
                <ExternalLink className="h-4 w-4" />
                View & Share Proposal
              </Button>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" onClick={handleOpenNewTab} className="gap-2">
                  <ExternalLink className="h-4 w-4" />
                  Open in New Tab
                </Button>
                <Button variant="outline" onClick={handleNewQuote} className="gap-2">
                  <Zap className="h-4 w-4" />
                  New Quote
                </Button>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
